/* global BigInt */

const normalizeValue = (value) => {
  if (value === undefined || value === null || value === '') return '';
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^0x[0-9a-fA-F]+$/.test(trimmed)) {
      try {
        return BigInt(trimmed).toString();
      } catch (error) {
        return trimmed.toLowerCase();
      }
    }
    return trimmed;
  }
  return String(value);
};

const normalizeAddress = (value) => String(value || '').trim().toLowerCase();

export const normalizeTransactionTemplateRequest = (transaction = {}) => ({
  to: normalizeAddress(transaction.to),
  data: String(transaction.data || '0x').trim().toLowerCase(),
  value: normalizeValue(transaction.value),
  gas: normalizeValue(transaction.gas),
  gasLimit: normalizeValue(transaction.gasLimit),
  gasPrice: normalizeValue(transaction.gasPrice),
  maxFeePerGas: normalizeValue(transaction.maxFeePerGas),
  maxPriorityFeePerGas: normalizeValue(transaction.maxPriorityFeePerGas),
  nonce: normalizeValue(transaction.nonce),
  chainId: normalizeValue(transaction.chainId),
  type: normalizeValue(transaction.type),
});

export const buildTransactionTemplateKey = ({
  chainId,
  rpcUrl,
  from,
  transaction,
  source = 'backend-wallet',
} = {}) => JSON.stringify({
  source: String(source || 'backend-wallet'),
  chainId: normalizeValue(chainId),
  rpcUrl: String(rpcUrl || '').trim(),
  from: normalizeAddress(from),
  transaction: normalizeTransactionTemplateRequest(transaction),
});

class TransactionTemplateService {
  constructor() {
    this.rawTemplates = new Map();
    this.calldataTemplates = new Map();
  }

  clear() {
    this.rawTemplates.clear();
    this.calldataTemplates.clear();
  }

  storeCalldataTemplate(key, template = {}) {
    if (!key || !template.txRequest) return null;
    const stored = {
      ...template,
      txRequest: { ...template.txRequest },
      expiresAt: Number(template.expiresAt || Date.now() + 5000),
      createdAt: template.createdAt || Date.now(),
    };
    this.calldataTemplates.set(key, stored);
    return stored;
  }

  peekCalldataTemplate(key, now = Date.now()) {
    const template = this.calldataTemplates.get(key);
    if (!template) return null;
    if (template.expiresAt && Number(template.expiresAt) <= now) {
      this.calldataTemplates.delete(key);
      return null;
    }
    return template;
  }

  storeRawTemplate(key, template = {}) {
    if (!key || !template.rawTx) return null;
    const stored = {
      ...template,
      rawTx: String(template.rawTx),
      expiresAt: Number(template.expiresAt || Date.now() + 5000),
      createdAt: template.createdAt || Date.now(),
    };
    this.rawTemplates.set(key, stored);
    return stored;
  }

  peekRawTemplate(key, now = Date.now()) {
    const template = this.rawTemplates.get(key);
    if (!template) return null;
    if (template.expiresAt && Number(template.expiresAt) <= now) {
      this.rawTemplates.delete(key);
      return null;
    }
    return template;
  }

  consumeRawTemplate(key, now = Date.now()) {
    const template = this.peekRawTemplate(key, now);
    if (!template) return null;
    this.rawTemplates.delete(key);
    return template;
  }
}

export const transactionTemplateService = new TransactionTemplateService();
